import React, { useState, useEffect, useMemo } from 'react';
import { axios } from '../../context/AuthContext';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts'; 

const TL = (v) => new Intl.NumberFormat('tr-TR', { 
  style: 'currency', 
  currency: 'TRY', 
  minimumFractionDigits: 0 
}).format(v || 0);

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function SozlesmeAnalizPage() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchData = async () => {
    try {
      const r = await axios.get('/muhasebe/sozlesmeler'); // Tüm sözleşmeleri çeken endpoint
      if (r.data.success) setData(r.data.data);
    } catch (err) {
      console.error("Veri yükleme hatası", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Veri Analiz Mantığı ─────────────────────────────────────────
  const analiz = useMemo(() => {
    const sinifGruplari = {};
    let toplamCiro = 0;
    let toplamTahsilat = 0;

    data.forEach(s => {
      const sinif = s.sinif_adi || 'Belirtilmemiş';
      const net = parseFloat(s.net_ucret) || 0;
      const odenen = parseFloat(s.odenen_tutar) || 0;

      if (!sinifGruplari[sinif]) {
        sinifGruplari[sinif] = { 
          isim: sinif, 
          toplamTutar: 0, 
          ogrenciSayisi: 0, 
          tahsilEdilen: 0 
        };
      }

      sinifGruplari[sinif].toplamTutar += net;
      sinifGruplari[sinif].tahsilEdilen += odenen;
      sinifGruplari[sinif].ogrenciSayisi += 1;
      toplamCiro += net;
      toplamTahsilat += odenen;
    });

    // Ortalama hesaplama
    const grafikVerisi = Object.values(sinifGruplari).map(g => ({
      ...g,
      ortalamaFiyat: g.toplamTutar / g.ogrenciSayisi
    }));

    return { grafikVerisi, toplamCiro, toplamTahsilat, toplamOgrenci: data.length };
  }, [data]);

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: isMobile ? '14px' : '20px' }}>
      <div className="section-header">
        <h2 style={{ fontWeight: 900 }}>📊 Sözleşme & Fiyat Analizi</h2>
        <div className="section-subtitle">Sınıf bazlı ortalama fiyatlar ve doluluk oranları</div>
      </div>

      {/* Üst Kartlar: Genel Özet */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
        <div className="card" style={{ textAlign: 'center', borderLeft: '4px solid #3B82F6' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Toplam Ciro</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{TL(analiz.toplamCiro)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', borderLeft: '4px solid #10B981' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Tahsil Edilen</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#10B981' }}>{TL(analiz.toplamTahsilat)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center', borderLeft: '4px solid #F59E0B' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Genel Ortalama Fiyat</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>
            {TL(analiz.toplamCiro / (analiz.toplamOgrenci || 1))}
          </div>
        </div>
      </div>

      <div className="muh-two-col" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 20 }}>
        {/* Grafik: Sınıf Bazlı Ortalama Fiyatlar */}
        <div className="card">
          <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>Sınıf Bazlı Ortalama Sözleşme Bedeli</h3>
          <div style={{ width: '100%', height: isMobile ? 240 : 300 }}>
            <ResponsiveContainer>
              <BarChart data={analiz.grafikVerisi}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="isim" />
                <YAxis tickFormatter={(v) => `₺${v/1000}k`} />
                <Tooltip formatter={(value) => TL(value)} />
                <Bar dataKey="ortalamaFiyat" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pasta Grafik: Öğrenci Dağılımı */}
        <div className="card">
          <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>Sınıf Dağılımı (Öğrenci)</h3>
          <div style={{ width: '100%', height: isMobile ? 240 : 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie 
                  data={analiz.grafikVerisi} 
                  dataKey="ogrenciSayisi" 
                  nameKey="isim" 
                  cx="50%" cy="50%" 
                  outerRadius={80} label
                >
                  {analiz.grafikVerisi.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detaylı Tablo Analizi */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <div style={{ padding: '16px', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>
          Sınıf Detay Analiz Listesi
        </div>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {analiz.grafikVerisi.map((g, i) => {
              const oran = g.toplamTutar > 0 ? Math.min((g.tahsilEdilen / g.toplamTutar) * 100, 100) : 0;
              return (
                <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: '#fff' }}>
                  <div style={{ fontWeight: 800 }}>{g.isim}</div>
                  <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{g.ogrenciSayisi} kişi</div>
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Toplam Ciro</div>
                      <div style={{ fontWeight: 800 }}>{TL(g.toplamTutar)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Ort. Fiyat</div>
                      <div style={{ fontWeight: 800, color: 'var(--blue)' }}>{TL(g.ortalamaFiyat)}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.75rem' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Tahsilat Oranı</span>
                      <span style={{ fontWeight: 700 }}>%{Math.round(oran)}</span>
                    </div>
                    <div style={{ height: 6, background: '#eee', borderRadius: 3 }}>
                      <div style={{ width: `${oran}%`, height: '100%', background: '#10B981', borderRadius: 3 }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Sınıf / Grup</th>
                <th>Öğrenci</th>
                <th>Toplam Ciro</th>
                <th>Tahsilat Oranı</th>
                <th>Ort. Fiyat</th>
              </tr>
            </thead>
            <tbody>
              {analiz.grafikVerisi.map((g, i) => {
                const oran = g.toplamTutar > 0 ? Math.min((g.tahsilEdilen / g.toplamTutar) * 100, 100) : 0;
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 700 }}>{g.isim}</td>
                    <td>{g.ogrenciSayisi} Kişi</td>
                    <td>{TL(g.toplamTutar)}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3 }}>
                          <div style={{ width: `${oran}%`, height: '100%', background: '#10B981', borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: '0.7rem' }}>
                          %{Math.round(oran)}
                        </span>
                      </div>
                    </td>
                    <td style={{ fontWeight: 800, color: 'var(--blue)' }}>{TL(g.ortalamaFiyat)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
